import { Icon } from '@cad-arc';
import { DexFormSectionView, DexPersonFormBootstrapView, UserProfileView } from '@mark43/cad-api';
import { FederatedSearchParsedResult } from 'mark43-federated-search/dist/lib/types';
import { DexInquiry } from './types';
import {
    DexResultView,
    DexEntityTypeEnumType,
    PersonInquiry,
    VehicleRegistrationInquiry,
} from '@mark43/dex-api';
import { colors } from '../colors/colors';

const DMV_KEYS_REGEX = /(^|[^A-Z0-9])(DMV|RMV|DOT)([^A-Z0-9]|$)/i;

export function isRmsOrDmvResult(federatedSearchParsedResult?: FederatedSearchParsedResult): boolean {
    return (
        !!federatedSearchParsedResult?.Mark43Id ||
        DMV_KEYS_REGEX.test(federatedSearchParsedResult?.IntSystemName || '') ||
        DMV_KEYS_REGEX.test(federatedSearchParsedResult?.System || '')
    );
}

export function formatName(userProfileView: UserProfileView): string {
    return `${userProfileView.firstName} ${userProfileView.lastName}`;
}

export function stripEmptyStringsAndTrim<T extends DexInquiry>(params: T): T {
    const dexParams = [...Object.entries(params)];

    const cleanParams = dexParams.map(([_, value]) => {
        let cleanValue = value;

        if (typeof value === 'string') {
            if (isEmptyString(value)) {
                cleanValue = undefined;
            } else {
                cleanValue = value.trim();
            }
        }

        return [_, cleanValue];
    });

    return Object.fromEntries(cleanParams) as T;
}

export function isNonEmptyString(value: string | undefined): boolean {
    return !!value && value.trim().length > 0;
}

function isEmptyString(value: string | undefined): boolean {
    return value !== undefined && value.trim().length === 0;
}

export function isCandidatesList(result: DexResultView): boolean {
    return result.drilldownOptions?.length > 0 && !!result.responseType;
}

export function isDrilldownResult(result: DexResultView): boolean {
    return !!result.drilldownTitle && !!result.drilldownIdentifier;
}

export function isHit(result?: DexResultView): boolean {
    if (!result) {
        return false;
    }
    return ['HIT', 'HOT_HIT'].includes(result.hitType);
}

export function isHotHit(result: DexResultView): boolean {
    return result.hitType === 'HOT_HIT';
}

export function getArcHitColor(isHit: boolean): string {
    return isHit ? colors.dexHit : 'var(--arc-colors-text-primary)';
}

export function getIconForDexEntityType(
    dexEntityType: DexEntityTypeEnumType
): React.ComponentProps<typeof Icon>['icon'] {
    switch (dexEntityType) {
        case 'ARTICLE':
            return 'Basket';
        case 'BOAT':
            return 'Boat';
        case 'FIREARM':
            return 'Firearm';
        case 'IMAGE':
            return 'Image';
        case 'PERSON':
            return 'Person';
        case 'VEHICLE':
            return 'Vehicle';
        case 'UNKNOWN':
            return 'DataExchangeResults';
        // don't know if CLIPS can show up, or what it is
        case 'CLIPS':
        default:
            return 'Vehicle';
    }
}

export function isParamsValid(params: DexInquiry, defaultParams: DexInquiry): boolean {
    let isValid = false;
    Object.keys(params).forEach((key) => {
        if (params[key as keyof DexInquiry] !== undefined) {
            if (typeof params[key as keyof DexInquiry] === 'number') {
                isValid = true;
            } else {
                if (
                    isNonEmptyString(params[key as keyof typeof params] as string) &&
                    !isNonEmptyString(defaultParams[key as keyof typeof defaultParams] as string)
                ) {
                    isValid = true;
                }
            }
        }
    });
    return isValid;
}

export function getDexFormKeys(sections: DexFormSectionView[]) {
    const fieldNameSet = new Set<string>();

    for (const section of sections) {
        for (const row of section.rows) {
            for (const field of row) {
                fieldNameSet.add(field.name);
            }
        }
    }

    return fieldNameSet;
}

/**
 * This function is used to ensure the prefill values we send over from the
 * "open DEx form" button on the event panel person and vehicle cards are aligned
 * with the fields present in that state's DEx form.
 *
 * This avoids a case where a value is prefilled but the user cannot see it as
 * it is not present in the form.
 */
export function pruneDexFields(
    bootstrap: DexPersonFormBootstrapView,
    prefillValues: PersonInquiry | VehicleRegistrationInquiry | undefined
) {
    if (!prefillValues) {
        return undefined;
    }

    const dexFormKeys = getDexFormKeys(bootstrap.dexFormView.sections);
    const prunedPrefillValues: PersonInquiry | VehicleRegistrationInquiry = { ...prefillValues };

    for (const fieldName of Object.keys(prefillValues)) {
        if (!dexFormKeys.has(fieldName)) {
            // @ts-expect-error This is OK, guaranteed to index string here.
            delete prunedPrefillValues[fieldName];
        }
    }

    return prunedPrefillValues;
}
